<template>
  <div data-test-id="form-simplified-enrollment-interests">
    <vf-notification
      v-if="notification.message"
      class="mb-4"
      :type="notification.type"
      @close="notification.message = ''"
    >
      {{ notification.message }}
    </vf-notification>
    <vf-progress-bar max="3" value="3" />
    <p v-if="showOptionsLead" class="mt-4" :class="brandClasses.description">
      {{ $t.interestsOptionsLead }}
    </p>
    <base-form v-slot="{ onChange }" class="mt-4" :form @submit.prevent="submit">
      <vf-form-field
        v-slot="{ attrs }"
        name="interests"
        :rule="[
          validateMinLength(1, $t.interestsSelection.min),
          validateMaxLength(3, $t.interestsSelection.max),
        ]"
      >
        <div class="flex gap-2 wrap">
          <vf-toggle-token
            v-for="(option, key) in options"
            v-bind="attrs"
            :key
            v-model="form.interests"
            :value="isArray(option) ? option[0] : option"
            @change="onChange"
          >
            {{ $t.interestsOptions[isArray(option) ? option[0] : option] }}
          </vf-toggle-token>
        </div>
      </vf-form-field>
      <div class="mt-6 space-y-2">
        <vf-button class="w-full" :loading type="submit">
          {{ $t.submit }}
        </vf-button>
        <vf-button class="w-full" :disabled="loading" variant="secondary" @click="emit('continue', false)">
          {{ $t.skipForNow }}
        </vf-button>
      </div>
    </base-form>
  </div>
</template>

<script lang="ts" setup>
import type { BaseNotification } from '#types/notification'

const props = defineProps<{
  interests?: string[]
}>()
const emit = defineEmits<{
  continue: [submitted: boolean]
}>()

const { brandClasses } = useAppConfig().components.dialog.simplifiedEnrollment
const { options, showOptionsLead } = useAppConfig().components.form.interests
const { $feature } = useNuxtApp()
const { updateInterests } = useUserStore()
const auth = useAuthStore()
const timelyDismissal = useTimelyDismissal()

const loading = ref(false)

const form = reactive({
  interests: props.interests || []
})

const notification = reactive<BaseNotification>({
  message: '',
  type: 'info'
})

onMounted(() => {
  if ($feature.tempEnablePostAuthOnboarding && auth.consumerNo)
    timelyDismissal.set(`se_interests_${auth.consumerNo}`)
})

const submit = async () => {
  try {
    loading.value = true
    notification.message = ''
    await updateInterests(form)
    emit('continue', true)
  }
  catch (err) {
    assertApiError(err)
    notification.message = err.message
    notification.type = 'error'
  }
  finally {
    loading.value = false
  }
}

watchEffect(() => {
  form.interests = form.interests.flatMap((interest) => options.includes(interest)
    ? interest
    : options.find((option) => isArray(option) && option[0] === interest)
  ).filter(Boolean) as string[]
})
</script>
